import Service, { service } from '@ember/service'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import { format, type NormalizedInterval } from 'date-fns'

import { assessmentScores } from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores'
import { assessmentScoresAverages } from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores-averages'
import { assessmentScoresHistory } from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores-history'
import { assessmentScoresByGenreHistory } from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores-by-genre-history'
import {
  assessmentScoresStudent,
  assessmentScoresByGenreStudent,
} from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores-student'
import { assignmentScores } from 'teacher-ui-client/utils/student-events/fetchers/assignment-scores'
import { awardsEarned } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned'
import { awardsEarnedByMonthEvents } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-by-month-events'
import { awardsEarnedHistory } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-history'
import { awardsEarnedTotals } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-totals'
import { booksReadByGenre } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-genre'
import { booksReadByGenreHistory } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-genre-history'
import { booksReadByGenreTotals } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-genre-totals'
import { booksReadByLevel } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-level'
import { booksReadByLevelHistory } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-level-history'
import { booksReadByLevelTotals } from 'teacher-ui-client/utils/student-events/fetchers/books-read-by-level-totals'
import { booksReadStudent } from 'teacher-ui-client/utils/student-events/fetchers/books-read-student'
import { certificateEvents } from 'teacher-ui-client/utils/student-events/fetchers/certificate-events'
import { courseProgressAverages } from 'teacher-ui-client/utils/student-events/fetchers/course-progress-averages'
import { courseProgressCategories } from 'teacher-ui-client/utils/student-events/fetchers/course-progress-categories'
import { courseProgressByGrade } from 'teacher-ui-client/utils/student-events/fetchers/course-progress-by-grade'
import {
  courseProgressLessonHistory,
  courseProgressSpellingHistory,
} from 'teacher-ui-client/utils/student-events/fetchers/course-progress-history'
import {
  courseProgressSpellingStudent,
  courseProgressLessonsStudent,
} from 'teacher-ui-client/utils/student-events/fetchers/course-progress-student'
import { courseProgressTotals } from 'teacher-ui-client/utils/student-events/fetchers/course-progress-totals'
import { drivingTests } from 'teacher-ui-client/utils/student-events/fetchers/driving-tests'
import { drivingTestDetailsTotalsByMonth } from 'teacher-ui-client/utils/student-events/fetchers/driving-test-details-totals-by-month'
import {
  drivingTestsHistoryRe,
  drivingTestsHistoryMs,
} from 'teacher-ui-client/utils/student-events/fetchers/driving-tests-history'
import { drivingTestDetailsHistory } from 'teacher-ui-client/utils/student-events/fetchers/driving-test-details-history'
import {
  drivingTestsTotalsRe,
  drivingTestsTotalsMs,
} from 'teacher-ui-client/utils/student-events/fetchers/driving-tests-totals'
import {
  drivingTestsStudentRe,
  drivingTestsStudentMs,
} from 'teacher-ui-client/utils/student-events/fetchers/driving-tests-student'
import { lexileDetailsAverages } from 'teacher-ui-client/utils/student-events/fetchers/lexile-details-averages'
import { lexileDetailsHistory } from 'teacher-ui-client/utils/student-events/fetchers/lexile-details-history'
import { lexileDetailsStudent } from 'teacher-ui-client/utils/student-events/fetchers/lexile-details-student'
import { lexileGrowth } from 'teacher-ui-client/utils/student-events/fetchers/lexile-growth'
import { mentalMinuteStudent } from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-student'
import { mentalMinuteOverview } from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-overview'
import { mentalMinuteSummary } from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-summary'
import { peaksProgress } from 'teacher-ui-client/utils/student-events/fetchers/peaks-progress'
import { peaksProgressDetails } from 'teacher-ui-client/utils/student-events/fetchers/peaks-progress-details'
import { quizScoresAverages } from 'teacher-ui-client/utils/student-events/fetchers/quiz-scores-averages'
import { quizScoresHistory } from 'teacher-ui-client/utils/student-events/fetchers/quiz-scores-history'
import { quizScoresStudent } from 'teacher-ui-client/utils/student-events/fetchers/quiz-scores-student'
import { readingMapDetails } from 'teacher-ui-client/utils/student-events/fetchers/reading-map-details'
import {
  simpleSummarySchool,
  simpleSummaryStudents,
} from 'teacher-ui-client/utils/student-events/fetchers/simple-summary'
import { skillsMasteredRe, skillsMasteredMs } from 'teacher-ui-client/utils/student-events/fetchers/skills-mastered'
import { spellingLessonDetails } from 'teacher-ui-client/utils/student-events/fetchers/spelling-lesson-details'
import { spellingMapDetails } from 'teacher-ui-client/utils/student-events/fetchers/spelling-map-details'
import { stadiumEventsHistory } from 'teacher-ui-client/utils/student-events/fetchers/stadium-events-history'
import { stadiumEvents } from 'teacher-ui-client/utils/student-events/fetchers/stadium-events'
import { stadiumEventsStudent } from 'teacher-ui-client/utils/student-events/fetchers/stadium-events-student'
import { storiesWrittenByGrade } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-by-grade'
import { stadiumEventsTotals } from 'teacher-ui-client/utils/student-events/fetchers/stadium-events-totals'
import { storiesWrittenDetails } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-details'
import { storiesWrittenHistory } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-history'
import { storiesWrittenStudent } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-student'
import { storylandsHistory } from 'teacher-ui-client/utils/student-events/fetchers/storylands-history'
import { storiesWrittenTotals } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-totals'
import { storylandsTotals } from 'teacher-ui-client/utils/student-events/fetchers/storylands-totals'
import { storylandsStudent } from 'teacher-ui-client/utils/student-events/fetchers/storylands-student'
import {
  summarySchoolRe,
  summarySchoolRex,
  summarySchoolMs,
  summarySchoolFp,
  summaryStudentsRe,
  summaryStudentsMs,
} from 'teacher-ui-client/utils/student-events/fetchers/summary'
import { studentSummary } from 'teacher-ui-client/utils/student-events/fetchers/student-summary'
import {
  timeOnTaskHistory,
  timeOnTaskHistoryV2,
} from 'teacher-ui-client/utils/student-events/fetchers/time-on-task-history'
import { timeOnTaskUsage, timeOnTaskUsageV2 } from 'teacher-ui-client/utils/student-events/fetchers/time-on-task-usage'
import { totalUsageOverTime } from 'teacher-ui-client/utils/student-events/fetchers/total-usage-over-time'
import { workingAtGradeLevel } from 'teacher-ui-client/utils/student-events/fetchers/working-at-grade-level'
import type { StatsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type AuthToken from '@blakeelearning/auth/services/auth-token'

/**
 * Service for interacting with the student-events api.
 */
export default class StudentEventsService extends Service {
  @service authToken!: AuthToken

  /**
   * Builds a url from a product, studentScope and endpoint.
   */
  buildUrl(
    endpoint: string,
    product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductFp | LegacyProductWl,
    studentScope: StatsScope,
  ): string {
    const joined = join(config.studentEventsV3Url, studentScope.scope, product, endpoint)
    return joined.href
  }

  /**
   * Builds a query object that is suitable for requests targetting the
   * student-events api from studentScope, period and base query objects.
   */
  buildQuery({ ids }: StatsScope, { start, end }: NormalizedInterval, base = {}): RequestInit {
    const fromTime = new Date(start)
    fromTime.setHours(0, 0, 0, 0) // start of day
    const fromTimeSeconds = Math.floor(fromTime.getTime() / 1000) // seconds since epoch

    const toTime = new Date(end)
    toTime.setHours(23, 59, 59, 999) // end of day
    const toTimeSeconds = Math.floor(toTime.getTime() / 1000) // seconds since epoch

    const tz = format(fromTime, 'xxx') // formats timezone offset like `+11:30`. For GMT, it's `+00:00`

    const data = {
      ...base,
      from_date: fromTimeSeconds,
      to_date: toTimeSeconds,
      tz,
      ids,
    }
    return {
      body: JSON.stringify(data),
      headers: { 'content-type': 'application/json' },
    }
  }

  /**
   * Fetches data from an API endpoint.
   */
  async fetchData<R>(input: RequestInfo | URL | string, requestInit: RequestInit): Promise<R | undefined> {
    try {
      const mergedRequestInit: RequestInit = {
        ...requestInit,
        body: requestInit.body,
        headers: { Authorization: this.authToken?.token ?? '', ...requestInit.headers },
      }
      const response = await fetch(input, {
        method: 'POST',
        ...mergedRequestInit,
      })

      if (response.ok) {
        return (await response.json()) as R
      }
      if (response.headers.get('content-type') === 'application/json') {
        throw await response.json()
      }
      throw new Error(await response.text())
    } catch (error) {
      if (error instanceof DOMException && error?.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
        return
      }
      console.error('Couldnt load the student events report', error)
      throw error
    }
  }

  assessmentScores = assessmentScores

  assessmentScoresAverages = assessmentScoresAverages

  assessmentScoresHistory = assessmentScoresHistory

  assessmentScoresByGenreHistory = assessmentScoresByGenreHistory

  assessmentScoresStudent = assessmentScoresStudent

  assessmentScoresByGenreStudent = assessmentScoresByGenreStudent

  assignmentScores = assignmentScores

  awardsEarned = awardsEarned

  awardsEarnedByMonthEvents = awardsEarnedByMonthEvents

  awardsEarnedHistory = awardsEarnedHistory

  awardsEarnedTotals = awardsEarnedTotals

  booksReadByGenre = booksReadByGenre

  booksReadByGenreHistory = booksReadByGenreHistory

  booksReadByGenreTotals = booksReadByGenreTotals

  booksReadByLevel = booksReadByLevel

  booksReadByLevelHistory = booksReadByLevelHistory

  booksReadByLevelTotals = booksReadByLevelTotals

  booksReadStudent = booksReadStudent

  certificateEvents = certificateEvents

  courseProgressAverages = courseProgressAverages

  courseProgressByGrade = courseProgressByGrade

  courseProgressCategories = courseProgressCategories

  courseProgressLessonHistory = courseProgressLessonHistory

  courseProgressSpellingHistory = courseProgressSpellingHistory

  courseProgressSpellingStudent = courseProgressSpellingStudent

  courseProgressLessonsStudent = courseProgressLessonsStudent

  courseProgressTotals = courseProgressTotals

  drivingTests = drivingTests

  drivingTestDetailsTotalsByMonth = drivingTestDetailsTotalsByMonth

  drivingTestDetailsHistory = drivingTestDetailsHistory

  drivingTestsHistoryRe = drivingTestsHistoryRe

  drivingTestsHistoryMs = drivingTestsHistoryMs

  drivingTestsStudentRe = drivingTestsStudentRe

  drivingTestsStudentMs = drivingTestsStudentMs

  drivingTestsTotalsRe = drivingTestsTotalsRe

  drivingTestsTotalsMs = drivingTestsTotalsMs

  peaksProgress = peaksProgress

  peaksProgressDetails = peaksProgressDetails

  lexileDetailsAverages = lexileDetailsAverages

  lexileDetailsHistory = lexileDetailsHistory

  lexileDetailsStudent = lexileDetailsStudent

  lexileGrowth = lexileGrowth

  mentalMinuteOverview = mentalMinuteOverview

  mentalMinuteStudent = mentalMinuteStudent

  mentalMinuteSummary = mentalMinuteSummary

  quizScoresAverages = quizScoresAverages

  quizScoresHistory = quizScoresHistory

  quizScoresStudent = quizScoresStudent

  readingMapDetails = readingMapDetails

  simpleSummarySchool = simpleSummarySchool

  simpleSummaryStudents = simpleSummaryStudents

  skillsMasteredRe = skillsMasteredRe

  skillsMasteredMaths = skillsMasteredMs

  spellingLessonDetails = spellingLessonDetails

  spellingMapDetails = spellingMapDetails

  stadiumEvents = stadiumEvents

  stadiumEventsHistory = stadiumEventsHistory

  stadiumEventsStudent = stadiumEventsStudent

  stadiumEventsTotals = stadiumEventsTotals

  storiesWrittenByGrade = storiesWrittenByGrade

  storiesWrittenDetails = storiesWrittenDetails

  storiesWrittenHistory = storiesWrittenHistory

  storiesWrittenStudent = storiesWrittenStudent

  storiesWrittenTotals = storiesWrittenTotals

  storylandsTotals = storylandsTotals

  summarySchoolRe = summarySchoolRe

  summarySchoolRex = summarySchoolRex

  summarySchoolMs = summarySchoolMs

  summarySchoolFp = summarySchoolFp

  summaryStudentsRe = summaryStudentsRe

  summaryStudentsMs = summaryStudentsMs

  studentSummary = studentSummary

  timeOnTaskHistory = timeOnTaskHistory

  timeOnTaskHistoryV2 = timeOnTaskHistoryV2

  timeOnTaskUsage = timeOnTaskUsage

  timeOnTaskUsageV2 = timeOnTaskUsageV2

  storylandsHistory = storylandsHistory

  storylandsStudent = storylandsStudent

  totalUsageOverTime = totalUsageOverTime

  workingAtGradeLevel = workingAtGradeLevel
}

declare module '@ember/service' {
  interface Registry {
    'student-events': StudentEventsService
  }
}
